import type {
	DesignPreset,
	DesignPresetProperty,
	DesignPresetPropertyValue,
	DesignPresetValue,
} from 'common/design-tokens';
import type { StoryDefaultPlatform } from 'types/story';

export const PRESET_PROPERTY_GROUPS = [
	{ id: 'border', label: 'Border', properties: ['borderStyle', 'borderWidth', 'borderColor'] },
	{
		id: 'radius',
		label: 'Radius',
		properties: [
			'borderRadius',
			'borderTopLeftRadius',
			'borderTopRightRadius',
			'borderBottomRightRadius',
			'borderBottomLeftRadius',
		],
	},
	{ id: 'outerSpace', label: 'Outer space', properties: ['marginTop', 'marginRight', 'marginBottom', 'marginLeft'] },
	{
		id: 'innerSpace',
		label: 'Inner space',
		properties: ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'],
	},
] as const satisfies readonly { id: string; label: string; properties: readonly DesignPresetProperty[] }[];
export type PresetPropertyGroup = {
	id: (typeof PRESET_PROPERTY_GROUPS)[number]['id'];
	label: string;
	properties: readonly DesignPresetProperty[];
};
/** The Add property menu, sectioned and ordered as the Inspector groups; rows are properties or group ids */
export const PRESET_PROPERTY_SECTIONS: readonly {
	label: string;
	rows: readonly (PresetPropertyGroup['id'] | DesignPresetProperty)[];
}[] = [
	{ label: 'Layout', rows: ['outerSpace', 'innerSpace'] },
	{ label: 'Border', rows: ['border', 'radius'] },
	{ label: 'Background', rows: ['backgroundColor'] },
	{
		label: 'Text',
		rows: [
			'fontFamily',
			'fontWeight',
			'fontSize',
			'lineHeight',
			'letterSpacing',
			'textTransform',
			'textDecoration',
			'textAlign',
			'direction',
			'color',
		],
	},
	{ label: 'Effects', rows: ['opacity', 'boxShadow', 'textShadow'] },
];
export type PresetAssignmentChange = { property: DesignPresetProperty; value: DesignPresetValue | undefined };

export type CapturedPresetSection = {
	label: string;
	/** Add property rows the section captured: a group id, such as Inner space, or a single property */
	rows: (PresetPropertyGroup['id'] | DesignPresetProperty)[];
	properties: DesignPresetProperty[];
	/** Where the captured values are assigned: Base and the platforms with their own values */
	platforms: ('base' | StoryDefaultPlatform)[];
};

const rowProperties = (row: PresetPropertyGroup['id'] | DesignPresetProperty): readonly DesignPresetProperty[] =>
	PRESET_PROPERTY_GROUPS.find(group => group.id === row)?.properties ?? [row as DesignPresetProperty];

/** Save as preset: captured properties by Add property section, each with the platforms it assigns */
export function getCapturedPresetSections(properties: DesignPreset['properties']): CapturedPresetSection[] {
	return PRESET_PROPERTY_SECTIONS.flatMap(section => {
		const rows = section.rows.filter(row => rowProperties(row).some(property => properties[property]));
		const present = rows.flatMap(rowProperties).filter(property => properties[property]);
		if (!present.length) return [];
		const platforms = new Set<'base' | StoryDefaultPlatform>();
		for (const property of present) {
			if (properties[property]?.base !== undefined) platforms.add('base');
			Object.keys(properties[property]?.platforms ?? {}).forEach(platform =>
				platforms.add(platform as StoryDefaultPlatform)
			);
		}
		return [{ label: section.label, rows, properties: present, platforms: [...platforms] }];
	});
}

/** The captured properties of the chosen sections only */
export function pickCapturedSections(
	properties: DesignPreset['properties'],
	sections: readonly CapturedPresetSection[]
): DesignPreset['properties'] {
	return Object.fromEntries(
		sections.flatMap(section => section.properties.map(property => [property, properties[property]]))
	);
}

/** Preserve authored row order and keep newly added empty groups at their insertion point. */
export function getPresetPropertyRowKeys(preset: DesignPreset, addedRows: readonly string[]): string[] {
	const existing = Object.keys(preset.properties).map(
		property =>
			PRESET_PROPERTY_GROUPS.find(group => group.properties.some(item => item === property))?.id ?? property
	);
	return [...new Set([...existing.filter(key => !addedRows.includes(key)), ...addedRows])];
}

/** Applies one user action atomically, without materializing other properties/platforms. */
export function updatePresetAssignments(
	preset: DesignPreset,
	platform: 'base' | StoryDefaultPlatform,
	changes: readonly PresetAssignmentChange[]
): DesignPreset {
	const properties = { ...preset.properties };
	for (const change of changes) {
		const assignment: DesignPresetPropertyValue = {
			...properties[change.property],
			platforms: { ...properties[change.property]?.platforms },
		};
		if (platform === 'base') {
			if (change.value === undefined) delete assignment.base;
			else assignment.base = change.value;
		} else if (change.value === undefined) delete assignment.platforms![platform];
		else assignment.platforms![platform] = change.value;
		if (!Object.keys(assignment.platforms ?? {}).length) delete assignment.platforms;
		if (assignment.base === undefined && !assignment.platforms) delete properties[change.property];
		else properties[change.property] = assignment;
	}
	return { ...preset, properties };
}
